import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { matchClient } from '../api/matchClient.js';
import { realtime } from '../api/realtime.js';
import { createCheckersRenderer } from '../ui/checkersRenderer.js';
import { createChessRenderer } from '../ui/chessRenderer.js';
import { createMancalaRenderer } from '../ui/mancalaRenderer.js';
import { createBackgammonRenderer } from '../ui/backgammonRenderer.js';
import { showRulesModal } from '../ui/RulesModal.js';
import { hasRules } from '../ui/gameRules.js';
import { CHECKERS_TEXTURES } from '../ui/checkersAssets.js';
import { showModal } from '../ui/Modal.js';
import { showToast } from '../ui/Toast.js';
import { audioManager } from '../audio/AudioManager.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { renderBackground } from '../render/world.js';
import { nameWithIcon } from '../ui/botLabel.mjs';
import { iconText } from '../ui/iconText.js';
import { serverNow } from '../state/serverClock.mjs';

const BOARD_SIZE = 480;

// Per-game board renderer. The scene stays game-agnostic beyond this lookup and the
// per-game captured-icon hook (renderer.capturedGlyphs) — new games register a factory
// with the same `update({board?, legalMoves, interactive, myColor, state})` surface.
const RENDERERS = {
  checkers: createCheckersRenderer, chess: createChessRenderer, mancala: createMancalaRenderer,
  backgammon: createBackgammonRenderer,
};

// Review fix pass, Finding 3: server rejections (match_error) had NO listener anywhere
// on the frontend — they silently vanished, so e.g. a stale OFFER_PENDING left the
// player staring at an unresponsive button with no explanation. Polish toast per code,
// with a generic fallback for any code added server-side later without a frontend update.
const MATCH_ERROR_MESSAGES = {
  ILLEGAL_MOVE: 'Nieprawidłowy ruch',
  NOT_YOUR_TURN: 'Nie twoja kolej',
  OFFER_PENDING: 'Propozycja remisu już czeka',
  NO_PENDING_OFFER: 'Brak propozycji remisu',
  NOT_A_PLAYER: 'Tylko gracze mogą to zrobić',
  CANNOT_CLAIM: 'Nie można jeszcze przejąć wygranej',
};
const MATCH_ERROR_FALLBACK = 'Nie można wykonać tej akcji';
// Tables t1–t3 live downstairs, t4–t7 upstairs. Used to send the player back to the
// right hall floor (still seated) when the match ends. Spawn points sit near each
// table; the hall scene rescues to its default if a point lands in an obstacle.
const HALL_RETURN = {
  t1: { scene: SCENE.GAME_HALL,    x: 445, y: 640 },
  t2: { scene: SCENE.GAME_HALL,    x: 777, y: 520 },
  t3: { scene: SCENE.GAME_HALL,    x: 1032, y: 500 },
  t4: { scene: SCENE.GAME_HALL_UP, x: 229, y: 490 },
  t5: { scene: SCENE.GAME_HALL_UP, x: 600, y: 600 },
  t6: { scene: SCENE.GAME_HALL_UP, x: 780, y: 430 },
  t7: { scene: SCENE.GAME_HALL_UP, x: 1130, y: 470 },
};

export class BoardGameScene extends Phaser.Scene {
  constructor() { super(SCENE.BOARD_GAME); }

  init(data) { this._game = data?.game ?? 'checkers'; }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx({ game: this._game })));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    this.matchId = data.matchId;
    this.game = data.game;
    this.tableId = data.tableId;
    this.myColor = data.myColor ?? null;          // refined from match_state.your_color
    this.players = data.players ?? {};            // { w:{id,name,is_bot}, b:{...} }
    this._over = false;
    this._prevBoard = null;                       // for capture/promotion sfx detection
    this._drawPending = false;                    // my own draw offer is awaiting a response

    renderBackground(this, 'bg_board_game');

    this._buildPanels();
    this._buildSpectatorBadge();
    this._buildResignButton();
    this._buildRulesButton();

    const createRenderer = RENDERERS[this.game] ?? createCheckersRenderer;
    this.renderer = createRenderer(this, {
      x: GAME_WIDTH / 2, y: GAME_HEIGHT / 2 + 30, size: BOARD_SIZE,
      myColor: this.myColor,
      onMove: (move) => matchClient.move(move),   // checkers sends a path array, chess a {from,to,promo?} dict
    });

    // Mobile / desktop cancel clears an in-progress multi-jump selection.
    virtualInput.registerCancel(() => this.renderer.clearSelection());

    this._unsubs = [
      matchClient.on('state', (m) => this._onState(m)),
      matchClient.on('over', (m) => this._onOver(m)),
      matchClient.on('opponent_disconnected', (m) => this._onOpponentDisconnected(m)),
      matchClient.on('opponent_reconnected', () => this._onOpponentReconnected()),
      matchClient.on('draw_offered', (m) => this._onDrawOffered(m)),
      matchClient.on('draw_declined', () => this._onDrawDeclined()),
      matchClient.on('error', (m) => this._onMatchError(m)),
    ];

    audioManager.playSfx('game_start');

    if (import.meta.env?.DEV) window.__board = this;   // e2e: inspect scene + renderer

    // Render whatever state we already have (buffered from match_started/resume).
    const initial = data.state ?? matchClient.state;
    if (initial) this._onState(initial);
    else this._refreshPanels(null);

    this.events.on('shutdown', () => this._cleanup());
  }

  // --- colour helpers -------------------------------------------------------------------
  _bottomColor() { return this.myColor === 'b' ? 'b' : 'w'; }   // MY colour sits at the bottom
  _topColor()    { return this._bottomColor() === 'w' ? 'b' : 'w'; }

  // --- panels ---------------------------------------------------------------------------
  _buildPanels() {
    this._leftPanel = this._makePanel(20, 18);
    this._rightPanel = this._makePanel(GAME_WIDTH - 20 - 320, 18);
  }

  _makePanel(px, py) {
    const W = 320, H = 92;
    const glow = this.add.rectangle(px, py, W, H, 0xffd166, 0).setOrigin(0, 0)
      .setStrokeStyle(4, 0xffd166, 0).setDepth(20);
    const bg = this.add.rectangle(px, py, W, H, 0x1a2230, 0.82).setOrigin(0, 0)
      .setStrokeStyle(2, 0x000000, 0.5).setDepth(20);
    const name = iconText(this, px + 12, py + 10, '', {
      fontFamily: 'system-ui', fontSize: '22px', color: '#fff', fontStyle: 'bold',
    }).setDepth(21);
    const turnLbl = iconText(this, px + W - 12, py + 12, '', {
      fontFamily: 'system-ui', fontSize: '15px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(21);
    const trophies = this.add.container(0, 0).setDepth(21);
    return { px, py, W, H, glow, bg, name, turnLbl, trophies };
  }

  _refreshPanels(state) {
    this._updatePanel(this._leftPanel, this._bottomColor(), state);
    this._updatePanel(this._rightPanel, this._topColor(), state);
  }

  _updatePanel(panel, color, state) {
    const p = this.players?.[color];
    let label = p?.name ? nameWithIcon(p.name) : (color === 'w' ? 'Gospodarz' : 'Gość');   // bots: word name + face icon
    if (color === this.myColor) label += ' (Ty)';
    panel.name.setText(label);

    // Turn glow + "Twoja kolej" on the side to move.
    const isTurn = !!state && state.state?.turn === color && !this._over;
    panel.glow.setStrokeStyle(4, 0xffd166, isTurn ? 1 : 0);
    panel.glow.setFillStyle(0xffd166, isTurn ? 0.1 : 0);
    panel.turnLbl.setText(isTurn ? (color === this.myColor ? 'Twoja kolej' : '…') : '');

    // Trophies: pieces THIS colour has taken (= opponent-colour pieces), as icons.
    panel.trophies.removeAll(true);
    const rowYcap = panel.py + panel.H - 22;

    // Per-game captured-icon hook: chess returns a Unicode glyph string derived from
    // the FEN (the framework's numeric captured count is {} / 0 for chess); other games
    // return null and fall through to the generic textured trophy row below.
    const glyphs = this.renderer?.capturedGlyphs?.(state?.state, color);
    if (glyphs != null) {
      panel.trophies.add(iconText(this, panel.px + 14, rowYcap, glyphs, {
        fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '20px',
        color: color === 'w' ? '#f2c9a0' : '#8fa3bf',   // my captures vs opponent's, kept legible on the dark panel
      }).setOrigin(0, 0.5).setDepth(21));
      return;
    }

    const n = state?.captured?.[color] ?? 0;
    const capturedTex = color === 'w' ? CHECKERS_TEXTURES.b : CHECKERS_TEXTURES.w;
    const rowY = panel.py + panel.H - 22;
    const show = Math.min(n, 12);
    for (let i = 0; i < show; i++) {
      panel.trophies.add(this.add.image(panel.px + 20 + i * 22, rowY, capturedTex).setDisplaySize(20, 20));
    }
    panel.trophies.add(iconText(this, panel.px + panel.W - 12, rowY, `${n}`, {
      fontFamily: 'system-ui', fontSize: '18px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(1, 0.5));
  }

  // --- spectator / resign ---------------------------------------------------------------
  _buildSpectatorBadge() {
    this._specBadge = iconText(this, GAME_WIDTH / 2, 26, 'Obserwujesz {eyes}', {
      fontFamily: 'system-ui', fontSize: '22px', color: '#fff', fontStyle: 'bold',
      backgroundColor: '#000000aa', padding: { x: 14, y: 6 },
    }).setOrigin(0.5, 0).setDepth(30).setVisible(false);
  }

  _buildResignButton() {
    // "Zaproponuj remis 🤝" sits left of "Poddaj się" — shift both off centre so they fit
    // side by side (Game Hall Plan 4, Task 2: game-agnostic draw offers).
    const drawX = GAME_WIDTH / 2 - 150, resignX = GAME_WIDTH / 2 + 110, y = GAME_HEIGHT - 40;
    this._resignBtn = this.add.rectangle(resignX, y, 200, 52, 0xaa3333).setStrokeStyle(2, 0x000000)
      .setDepth(30).setInteractive({ useHandCursor: true }).setVisible(false);
    this._resignTxt = iconText(this, resignX, y, 'Poddaj się', {
      fontFamily: 'system-ui', fontSize: '22px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(31).setVisible(false);
    this._resignBtn.on('pointerup', async () => {
      if (isInputLocked(this) || this._over) return;
      const ok = await showModal(this, { title: 'Poddajesz się?', body: 'Przeciwnik wygra tę partię.', confirmLabel: 'Poddaj się', cancelLabel: 'Graj dalej' });
      if (ok && !this._over) matchClient.resign();
    });

    this._drawBtn = this.add.rectangle(drawX, y, 260, 52, 0x2e7d32).setStrokeStyle(2, 0x000000)
      .setDepth(30).setInteractive({ useHandCursor: true }).setVisible(false);
    this._drawTxt = iconText(this, drawX, y, 'Zaproponuj remis {handshake}', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(31).setVisible(false);
    this._drawBtn.on('pointerup', () => {
      if (isInputLocked(this) || this._over || this._drawPending) return;
      matchClient.offerDraw();
      this._drawPending = true;
      showToast(this, 'Zaproponowano remis…');
      this._syncControls();
    });

    // Spectators can't resign — they get a plain exit that stands them up and
    // returns to the hall (players leave only via resign / game end).
    const x = GAME_WIDTH / 2;
    this._leaveBtn = this.add.rectangle(x, y, 220, 52, 0x555577).setStrokeStyle(2, 0x000000)
      .setDepth(30).setInteractive({ useHandCursor: true }).setVisible(false);
    this._leaveTxt = iconText(this, x, y, 'Wyjdź {door}', {
      fontFamily: 'system-ui', fontSize: '22px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(31).setVisible(false);
    this._leaveBtn.on('pointerup', () => {
      if (isInputLocked(this)) return;
      realtime.tableStand();
      this._returnToHall();
    });
  }

  // "📖 Instrukcja" on the free left strip beside the board — players AND
  // spectators (it's pure information), every game with an entry in gameRules.js.
  _buildRulesButton() {
    if (!hasRules(this.game)) return;
    const x = 110, y = GAME_HEIGHT / 2 + 30;
    this.add.rectangle(x, y, 180, 52, 0x8a6d3b).setStrokeStyle(2, 0x000000)
      .setDepth(30).setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        if (isInputLocked(this)) return;
        showRulesModal(this, this.game);
      });
    iconText(this, x, y, '{book} Instrukcja', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(31);
  }

  _syncControls() {
    const amPlayer = this.myColor === 'w' || this.myColor === 'b';
    const showResign = amPlayer && !this._over;
    this._resignBtn.setVisible(showResign);
    this._resignTxt.setVisible(showResign);
    const opponentColor = this.myColor === 'w' ? 'b' : 'w';
    const opponentIsBot = this.players?.[opponentColor]?.is_bot ?? false;
    const showDraw = amPlayer && !this._over && !this._drawPending && !opponentIsBot;
    this._drawBtn.setVisible(showDraw);
    this._drawTxt.setVisible(showDraw);
    const showLeave = !amPlayer && !this._over;
    this._leaveBtn.setVisible(showLeave);
    this._leaveTxt.setVisible(showLeave);
    this._specBadge.setVisible(!amPlayer && !this._over);
  }

  // --- draw offers ------------------------------------------------------------------------
  async _onDrawOffered(m) {
    if (this._over || m.by === window.__myId) return;   // my own offer echoed back — ignore
    if (!this.myColor) return;                          // spectators don't respond to draw offers
    const ok = await showModal(this, {
      title: 'Propozycja remisu', body: `${m.by_name} proponuje remis`,
      confirmLabel: 'Przyjmij', cancelLabel: 'Odrzuć',
    });
    if (!this._over) matchClient.respondDraw(ok);
  }

  _onDrawDeclined() {
    this._drawPending = false;
    showToast(this, 'Remis odrzucony');
    this._syncControls();
  }

  // --- server rejections ------------------------------------------------------------------
  _onMatchError(m) {
    // _drawPending is deliberately left as-is here (not reset) for both draw-related
    // codes: OFFER_PENDING means the server already believes an offer is outstanding
    // (possibly ours, about to be cleared by the next move on either side — no local
    // state to fix), and NO_PENDING_OFFER only ever arrives from a respondDraw() call,
    // which doesn't touch _drawPending at all. Just surface the toast.
    showToast(this, MATCH_ERROR_MESSAGES[m.error_code] ?? MATCH_ERROR_FALLBACK);
  }

  // --- match events ---------------------------------------------------------------------
  _onState(m) {
    if (m.your_color !== undefined) this.myColor = m.your_color;
    // A fresh match_state only ever arrives after a move was applied (offering a draw
    // does not emit one) — clears a pending offer of mine the same way the backend
    // expiry does for an opponent's move.
    this._drawPending = false;
    const board = m.state?.board ?? [];
    this._playMoveSfx(this._prevBoard, board, this._prevState, m.state);
    this._prevBoard = board.slice();
    this._prevState = m.state;                 // for the over-overlay re-render (chess: keep the FEN board)

    const myTurn = this.myColor && m.state?.turn === this.myColor && !this._over;
    this.renderer.update({
      board,
      legalMoves: myTurn ? (m.legal_moves ?? []) : [],
      interactive: !!myTurn,
      myColor: this.myColor,
      state: m.state,                          // chess derives its board + check flag from here
    });
    this._refreshPanels(m);
    this._syncControls();
  }

  // move_piece on any simple move; capture_piece when the piece count dropped; promote_piece
  // when a new king appeared (takes precedence — it's the most salient event).
  // A renderer that supplies moveSfx (chess: state carries a FEN, not a board array,
  // so the count heuristic below is blind there) picks the key itself from the states.
  _playMoveSfx(prev, next, prevState, nextState) {
    if (this.renderer?.moveSfx) {
      if (prevState) audioManager.playSfx(this.renderer.moveSfx(prevState, nextState));
      return;                                 // (no prevState = first render — no sound)
    }
    if (!prev) return;                        // first render — no sound
    const count = (b) => b.reduce((a, v) => a + (v ? 1 : 0), 0);
    const kings = (b) => b.reduce((a, v) => a + (v === 'W' || v === 'B' ? 1 : 0), 0);
    if (kings(next) > kings(prev)) audioManager.playSfx('promote_piece');
    else if (count(next) < count(prev)) audioManager.playSfx('capture_piece');
    else audioManager.playSfx('move_piece');
  }

  _onOver(m) {
    if (this._over) return;                   // match_over arrives on both table + floor rooms
    this._over = true;
    this._drawPending = false;
    this.renderer.update({ board: this._prevBoard ?? [], legalMoves: [], interactive: false, myColor: this.myColor, state: this._prevState });
    this._hideDisconnectOverlay();
    this._syncControls();

    let title, iWon = false;
    if (m.result === 'draw') { title = 'Remis'; }
    else if (m.result === 'void') { title = 'Gra przerwana'; }
    else if (this.myColor && m.winner === this.myColor) { title = 'Wygrana! {trophy}'; iWon = true; }
    else if (this.myColor) { title = 'Przegrana'; }
    else { title = m.winner_name ? `Wygrał: ${nameWithIcon(m.winner_name)}` : 'Koniec gry'; }

    audioManager.playSfx(iWon ? 'game_win' : 'game_draw');
    this._showOverOverlay(title);
  }

  _showOverOverlay(title) {
    const layer = this.add.container(0, 0).setDepth(2000);
    const overlay = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.62).setOrigin(0, 0).setInteractive();
    const box = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 460, 240, 0xffffff).setStrokeStyle(4, 0x333333);
    const t = iconText(this, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, title, {
      fontFamily: 'system-ui', fontSize: '36px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5);
    const btn = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 50, 260, 56, 0xffd166)
      .setStrokeStyle(2, 0x333333).setInteractive({ useHandCursor: true });
    const btnTxt = iconText(this, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 50, 'Wróć do salonu', {
      fontFamily: 'system-ui', fontSize: '22px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([overlay, box, t, btn, btnTxt]);
    btn.on('pointerup', () => this._returnToHall());
  }

  // --- disconnect / reconnect -----------------------------------------------------------
  _onOpponentDisconnected(m) {
    if (this._over) return;
    this._deadlineTs = m.deadline_ts;
    if (!this._dcLayer) {
      this._dcLayer = this.add.container(0, 0).setDepth(1500);
      const box = this.add.rectangle(GAME_WIDTH / 2, 120, 460, 108, 0x1a2230, 0.95).setStrokeStyle(2, 0xffd166);
      this._dcText = iconText(this, GAME_WIDTH / 2, 96, '', {
        fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold', align: 'center',
      }).setOrigin(0.5);
      this._claimBtn = this.add.rectangle(GAME_WIDTH / 2, 140, 220, 40, 0x555555).setStrokeStyle(2, 0x000000);
      this._claimTxt = iconText(this, GAME_WIDTH / 2, 140, 'Przejmij wygraną', {
        fontFamily: 'system-ui', fontSize: '17px', color: '#fff', fontStyle: 'bold',
      }).setOrigin(0.5);
      this._claimBtn.on('pointerup', () => {
        if (this._claimReady && !this._over) matchClient.claimWin();
      });
      this._dcLayer.add([box, this._dcText, this._claimBtn, this._claimTxt]);
    }
    this._dcLayer.setVisible(true);
    this._dcTick();
    this._dcTimer?.remove();
    this._dcTimer = this.time.addEvent({ delay: 500, loop: true, callback: () => this._dcTick() });
  }

  _dcTick() {
    if (!this._dcLayer || this._over) return;
    const remaining = Math.max(0, Math.ceil((this._deadlineTs * 1000 - serverNow()) / 1000));
    this._claimReady = remaining <= 0;
    this._dcText.setText(this._claimReady
      ? 'Przeciwnik rozłączony.\nMożesz przejąć wygraną.'
      : `Przeciwnik rozłączony…\nczekam ${remaining}s`);
    this._claimBtn.setFillStyle(this._claimReady ? 0x2e7d32 : 0x555555);
    this._claimBtn.setInteractive({ useHandCursor: this._claimReady });
    this._claimTxt.setColor(this._claimReady ? '#fff' : '#aaa');
  }

  _onOpponentReconnected() { this._hideDisconnectOverlay(); }

  _hideDisconnectOverlay() {
    this._dcTimer?.remove(); this._dcTimer = null;
    this._dcLayer?.setVisible(false);
  }

  // --- teardown -------------------------------------------------------------------------
  _returnToHall() {
    const dest = HALL_RETURN[this.tableId] ?? { scene: SCENE.GAME_HALL, x: 180, y: 600 };
    this.scene.start(dest.scene, { spawnX: dest.x, spawnY: dest.y });
  }

  _cleanup() {
    this._unsubs?.forEach((u) => u?.());
    this._dcTimer?.remove();
    virtualInput.clearCancel();
    this.renderer?.destroy();
    matchClient.detach();
  }
}
